import React from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { LoadingButton } from "components/ui";
import { InputBase } from "@mui/material";
import { makeStyles } from "@mui/styles";
import Send from "@mui/icons-material/Send";
import useTranslate from "hooks/use-translate-language";
import { sendTypingSignal } from "actions/twilio-chat";
import selectors from "selectors";

const useStyles = makeStyles(() => ({
  root: {
    padding: "0px",
    display: "flex",
    alignItems: "center",
    margin: "1px 2px",
    width: "100%"
  },
  input: {
    marginLeft: 1,
    marginBottom: 0,
    flex: 1
  },
  divider: {
    margin: 2
  },
  typingIndicator: {
    backgroundColor: "#FFFFFF",
    padding: "4px 16px",
    fontSize: "0.875rem",
    fontStyle: "italic",
    color: "#555555",
    lineHeight: 1.5,
    minHeight: "28px",
    display: "flex",
    alignItems: "center",
  }
}));

const SendMsgInput = ({ sendMessage, onTyping, typingParticipants = [], onInputFocus }) => {
  const translate = useTranslate();
  const dispatch = useDispatch();
  const [inputValue, setInputValue] = React.useState("");
  const [isSending, setIsSending] = React.useState(false);
  const classes = useStyles();
  const typingTimeoutRef = React.useRef(null);
  const userId = useSelector(selectors.getUserId);
  const othersTyping = typingParticipants.filter(id => id !== userId);

  React.useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div>
      {othersTyping.length > 0 && (
        <div
          className={classes.typingIndicator}
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {translate("coachIsTyping")}
        </div>
      )}
      <form
        className="send-msg-container"
        onSubmit={async event => {
        event.preventDefault();
        try {
          setIsSending(true);
          await sendMessage(inputValue);
          setInputValue("");
          // Clear typing throttle when message is sent
          if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
            typingTimeoutRef.current = null;
          }
        } finally {
          setIsSending(false);
        }
      }}
      style={{ margin: "0", border: "1px solid #ccc" }}
    >
      <InputBase
        className={classes.input}
        placeholder={translate("messagePlaceholder")}
        inputProps={{ "aria-label": "Send Chat Message" }}
        onChange={({ target }) => {
          setInputValue(target.value);
          if (!typingTimeoutRef.current) {
            if (onTyping) {
              onTyping();
            } else {
              dispatch(sendTypingSignal());
            }
            typingTimeoutRef.current = setTimeout(() => {
              typingTimeoutRef.current = null;
            }, 2000);
          }
        }}
        onFocus={() => {
          if (onInputFocus) {
            onInputFocus();
          }
        }}
        value={inputValue}
      />
      <LoadingButton
        color="primary"
        type="submit"
        isFetching={isSending}
        disabled={!inputValue}
      >
        <Send />
      </LoadingButton>
    </form>
    </div>
  );
};

SendMsgInput.propTypes = {
  sendMessage: PropTypes.func.isRequired,
  onTyping: PropTypes.func,
  typingParticipants: PropTypes.arrayOf(PropTypes.string),
  onInputFocus: PropTypes.func
};

export default SendMsgInput;
